/**
 * The three configuration warnings of `OpenXRCompositionLayer::get_configuration_warnings()`
 * (openxr_composition_layer.cpp:759-778), all decidable from the `.tscn`. One registration reaches
 * every subclass through `applicableNodeTypeMatcher`, since RuleRegistry matches
 * `applicableNodeTypes` by exact name.
 */
import { ruleRegistry } from '../../../../linter/RuleRegistry.js';
import { descendsFromClass } from '../../../../godot/classdb/classBaseTypes.js';
import { isExplicitlyHidden, parentTypeVerdict, placementPhrase } from '../../../../linter/parentType.js';
import { isOrthonormalTransform } from '../../../../linter/transformBasis.js';
import { ruleInt } from '../../../../godot/variant/intSlots.js';
import { boolSlotValue } from '../../../../godot/variant/variantBool.js';
import { armEmits, groundedArm, reportArm } from '../../../../linter/ruleArms.js';
const arms = {
    parentNotXROrigin3D: groundedArm('openxrcompositionlayer-parent-not-xrorigin3d', {
        kind: 'configuration-warning',
    }),
    nonOrthonormalTransform: groundedArm('openxrcompositionlayer-non-orthonormal-transform', {
        kind: 'configuration-warning',
    }),
    holePunchSortOrder: groundedArm('openxrcompositionlayer-hole-punch-sort-order', {
        kind: 'configuration-warning',
    }),
};
// Not modelled: set_layer_viewport's ERR_FAIL_COND_MSG when `use_android_surface` is true (:303-305).
// `layer_viewport` is declared at :151, before `use_android_surface` at :152, so a saved file sets it
// while `use_android_surface` is still false. A co-occurrence rule would fire on files Godot loads,
// and would depend on the keys' textual order.
function checkOpenXRCompositionLayer(context) {
    // No applicability check here: RuleRegistry has already filtered by the
    // matcher below, so re-asserting it states the same fact twice and the two
    // can drift.
    const { node, scene } = context;
    const properties = node.rawProperties;
    const diagnostics = [];
    // openxr_composition_layer.cpp:762-767 casts `get_parent()` with no `parent &&` guard, unlike
    // XRCamera3D, so this warns at the scene root too. An `unknowable` parent (instanced or untyped)
    // stays silent, since a guess would be a false positive on a legal scene.
    if (!isExplicitlyHidden(node)) {
        const verdict = parentTypeVerdict(scene, node, 'XROrigin3D');
        if (verdict.kind === 'mismatch' || verdict.kind === 'root') {
            const where = placementPhrase(verdict);
            reportArm(diagnostics, arms.parentNotXROrigin3D, node, `${node.type} '${node.name}' is ${where}. OpenXR composition layers must have an XROrigin3D node as their parent, the same configuration warning Godot's own editor reports.`);
        }
    }
    // openxr_composition_layer.cpp:769-771. Absent `transform` is Node3D's
    // identity default, which is trivially orthonormal, so only a present,
    // parseable literal can trip this.
    if (properties.transform !== undefined) {
        const orthonormal = isOrthonormalTransform(properties.transform);
        if (orthonormal === false) {
            reportArm(diagnostics, arms.nonOrthonormalTransform, node, `${node.type} '${node.name}' has a non-orthonormalized transform (scale or shearing). OpenXR composition layers must have orthonormalized transforms, the same configuration warning Godot's own editor reports.`);
        }
    }
    // openxr_composition_layer.cpp:773-775. `enable_hole_punch` defaults false
    // (openxr_composition_layer.h:88) and `sort_order` defaults 1 (openxr_composition_layer.h:90), so an
    // explicit `enable_hole_punch = true` with sort_order omitted still warns: the trigger is an
    // explicit opt-in, so `default-omitted` does not apply.
    const holePunchEnabled = boolSlotValue(properties.enable_hole_punch) === true;
    const sortOrder = ruleInt(properties.sort_order, 1);
    if (holePunchEnabled && sortOrder !== null && sortOrder >= 0) {
        reportArm(diagnostics, arms.holePunchSortOrder, node, `${node.type} '${node.name}' has enable_hole_punch on with sort_order ${sortOrder} (>= 0). Hole punching won't work as expected unless the sort order is less than zero, the same configuration warning Godot's own editor reports.`);
    }
    return diagnostics;
}
const openXRCompositionLayerValidationRule = {
    meta: {
        name: 'valid-openxrcompositionlayer',
        description: "Godot's three OpenXRCompositionLayer configuration warnings: parent must be XROrigin3D, transform must be orthonormal, and hole punching needs a negative sort order",
        category: 'validation',
        applicableNodeTypeMatcher: (nodeType) => descendsFromClass(nodeType, 'OpenXRCompositionLayer'),
        emits: armEmits(arms),
    },
    check: checkOpenXRCompositionLayer,
};
ruleRegistry.register(openXRCompositionLayerValidationRule);
//# sourceMappingURL=linter.js.map